import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Mail, ShieldCheck, Loader2, ArrowLeft, Send } from 'lucide-react';

/* Forgot Password
   - Email input → POST /api/auth/forgot-password (always 200, no enumeration)
   - Success state shows "check your email" with no leak about whether the
     account actually existed. Re-send is rate-limited server-side. */
export default function ForgotPasswordPage() {
  const { api } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error('Enter your email first');
    setLoading(true);
    try {
      await api.post('/api/auth/forgot-password', { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Could not send reset email. Try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: '#0a0b0f' }}
      data-testid="forgot-password-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-teal-400/10 border border-teal-400/20 mb-4">
            <ShieldCheck className="text-teal-400" size={26} />
          </div>
          <h1 className="text-3xl font-semibold text-white">Forgot password?</h1>
          <p className="text-sm text-slate-400 mt-2">
            We'll email you a secure link to set a new one.
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur p-8">
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-password-sent">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center">
                <Send className="text-emerald-300" size={20} />
              </div>
              <h2 className="text-xl font-semibold text-white">Check your inbox</h2>
              <p className="text-sm text-slate-400 leading-relaxed">
                If an account exists for <span className="text-teal-300">{email}</span>, a reset link is on its way. The link expires in 1 hour.
              </p>
              <p className="text-xs text-slate-500 leading-relaxed">
                Didn't get it? Check spam, or wait 60 seconds and try again.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-teal-300 hover:text-teal-200"
                data-testid="back-to-login-from-sent"
              >
                <ArrowLeft size={14} /> Back to login
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">Email</label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-3 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-teal-400/50"
                    data-testid="forgot-password-email"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-teal-400 text-[#0a0b0f] font-semibold hover:bg-teal-500 transition disabled:opacity-50"
                data-testid="forgot-password-submit"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Send reset link
              </button>
              <div className="text-center pt-2">
                <Link to="/login" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1.5" data-testid="back-to-login">
                  <ArrowLeft size={13} /> Back to login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
